import React from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import AddIcon from '@atlaskit/icon/glyph/add';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import CheckIcon from '@atlaskit/icon/glyph/check';
import Lozenge from '@atlaskit/lozenge';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button';
import { Radio } from '@atlaskit/radio';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { token } from '@atlaskit/tokens';
import { isStatusPageAddOnProduct, pluralize } from '@css-xp/ppc-utils';
import { ROVO_PRODUCT_ID, STATUSPAGE_ADDON_PRODUCT_ID } from '@css-xp/ppc-constants';
import {
  EntitlementIcon,
  InactiveLozenge,
  SandboxLozenge,
  TrialLozenge,
  AnnualLozenge,
  UpgradePremiumTrial,
} from '@css-xp/base';
import { BillingFrequency } from '@css-xp/graphtypes/src/generated-types-new';
import type { EntitlementItemType, SelectType } from '../types';
import { InactiveReasonStrings } from '../store-utils';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import Checkbox from '@atlaskit/checkbox';
import { useStore } from '../../../../store';
import { CartProductSchema } from '../../../../types';

const EntitlementProductIcon = styled.div`
  padding-right: 8px;
  display: flex;
  align-items: center;
  span {
    width: 24px;
  }
`;

const appRowStyles = ({ isDisabled = false }: { isDisabled?: boolean }) =>
  xcss({
    paddingTop: 'space.100',
    paddingBottom: 'space.100',
    opacity: isDisabled ? 0.5 : 1,
  });

const EntitlementAppIcon = styled.div`
  padding-right: 8px;
  padding-left: 64px;
  display: flex;
  align-items: center;
`;

const EntitlementInstancesIcon = styled.div`
  padding-right: 8px;
  padding-left: 64px;
  display: flex;
  align-items: center;
  span {
    height: 16px !important;
    width: 16px !important;
  }
`;

const ExpandableIconStyles = styled.div`
  width: 32px;
`;

const LozengeStyles = styled.div`
  padding-left: ${token('space.050')};
`;

type Props = EntitlementItemType & {
  onChange: (id: string, status: boolean) => void;
  isDisabled?: boolean;
  shouldShowAppsCount?: boolean;
  shouldShowTrialStatus?: boolean;
  shouldShowAddons?: boolean;
  shouldShowSandboxStatus?: boolean;
  isInactive?: boolean;
  inactiveReason?: string;
  checkedItemMap?: Record<string, SelectType>;
  alreadyCheckedItemMap?: Record<string, SelectType>;
  containerId?: string;
  version?: number;
  billingCycle?: string;
  isDefaultExpanded?: boolean;
  isOnUpgradeTrial?: boolean;
  isSingleProductSelection?: boolean;
  orgProductInstancesMap?: Record<string, CartProductSchema[]>;
  isTWCPhase2Enabled?: boolean;
  logoId?: string | null;
};

const CTAButton = styled(Button)`
  width: 32px;
  height: 32px;
`;

export const EntitlementItemV2 = ({
  id,
  name,
  productId,
  linkedEntitlements,
  addons,
  inTrial,
  isSandbox,
  onChange,
  containerId,
  billingCycle,
  entitlementNumber,
  isChecked,
  isDisabled,
  shouldShowAppsCount,
  shouldShowTrialStatus,
  shouldShowAddons,
  shouldShowSandboxStatus,
  inactiveReason,
  checkedItemMap,
  alreadyCheckedItemMap,
  version,
  isDefaultExpanded = false,
  isOnUpgradeTrial,
  isSingleProductSelection,
  orgProductInstancesMap,
  isTWCPhase2Enabled,
  logoId,
}: Props) => {
  const _onChange = (isSelected: boolean) => {
    entitlementNumber && onChange(id, isSelected);
  };
  const isInactive = InactiveReasonStrings.INACTIVE === inactiveReason;

  const [isExpanded, setIsExpanded] = React.useState<boolean>(isDefaultExpanded);
  //fetch apps data
  const [state] = useStore();
  const orgInstances = orgProductInstancesMap?.[id];
  const appsData = state?.linkedEntitlements;
  const appDetails = appsData?.[containerId ?? id];

  const productRow = (
    <Flex alignItems="center" justifyContent="space-between" key={id}>
      <Flex alignItems="center">
        {isSingleProductSelection ? (
          <Radio
            name="product-radio-select"
            isDisabled={isDisabled}
            isChecked={!!checkedItemMap?.[id]}
            onChange={() => onChange(id, true)}
          />
        ) : (
          <Checkbox
            isChecked={!!(checkedItemMap?.[id] || alreadyCheckedItemMap?.[id])}
            onChange={() => _onChange(!checkedItemMap?.[id])}
            label=""
            value="Controlled Checkbox"
            name="controlled-checkbox"
            isDisabled={isDisabled || !!alreadyCheckedItemMap?.[id]}
          />
        )}
        <Flex alignItems="center" xcss={xcss({ opacity: isDisabled ? 0.5 : 1 })}>
          <ExpandableIconStyles>
            {((isTWCPhase2Enabled && orgInstances && orgInstances?.length > 0) ||
              (shouldShowAppsCount && linkedEntitlements?.length > 0)) && (
              <Button
                className="expandable-row-button"
                iconBefore={isExpanded ? <ChevronDownIcon label="collapse" /> : <ChevronRightIcon label="expand" />}
                appearance="subtle"
                onClick={() => setIsExpanded(!isExpanded)}
                testId="expandable-row-button"
              />
            )}
          </ExpandableIconStyles>
          <EntitlementProductIcon>
            <EntitlementIcon width="24px" height="24px" productId={productId} isCloud logoId={logoId} />
          </EntitlementProductIcon>
          <Text>{name}</Text>
        </Flex>
        <Box paddingInlineStart="space.100">
          {isInactive && (
            <span data-testId="entitlement-inactive-lozenge">
              <InactiveLozenge label="Deactivated" />
            </span>
          )}
          {shouldShowSandboxStatus && isSandbox && productId !== ROVO_PRODUCT_ID && (
            <span data-testId="entitlement-sandbox-lozenge">
              <SandboxLozenge />
            </span>
          )}
          {shouldShowTrialStatus && inTrial && (
            <span data-testId="entitlement-trial-lozenge">
              <TrialLozenge />
            </span>
          )}
          {isOnUpgradeTrial && (
            <span data-testId="entitlement-upgrade-trial-lozenge">
              <UpgradePremiumTrial />
            </span>
          )}
          {billingCycle === BillingFrequency.ANNUAL && (
            <span data-testId="entitlement-annual-lozenge">
              <AnnualLozenge />
            </span>
          )}
          {shouldShowAddons && addons?.length > 0 && (
            <LozengeStyles>
              <span data-testId="add-on-lozenge">
                <Lozenge testId={'product-integrated-apps'} appearance={'default'}>
                  {pluralize(addons.length, 'Add-on')}
                </Lozenge>
              </span>
            </LozengeStyles>
          )}
        </Box>
      </Flex>
      <Flex alignItems="center" xcss={xcss({ opacity: isDisabled ? 0.5 : 1 })}>
        <Text size="small" color="color.text.subtlest">
          {entitlementNumber}
        </Text>
      </Flex>
    </Flex>
  );

  const appRows =
    shouldShowAppsCount && linkedEntitlements?.length > 0
      ? linkedEntitlements.map(({ id }: { id: string }) => {
          const selectedLinkedEntitlements = appDetails?.find((app: any) => app.id === id);
          if (selectedLinkedEntitlements) {
            const { entitlementNumber, logoId, name, productId } = selectedLinkedEntitlements;
            return (
              <Flex xcss={appRowStyles({ isDisabled })} alignItems="center" justifyContent="space-between" key={id}>
                <Flex alignItems="center">
                  <EntitlementAppIcon>
                    <EntitlementIcon
                      width="16px"
                      height="16px"
                      // this is required for statuspage addons to show correct icon
                      productId={isStatusPageAddOnProduct(productId) ? STATUSPAGE_ADDON_PRODUCT_ID : productId}
                      logoId={logoId}
                      isCloud
                    />
                  </EntitlementAppIcon>
                  <Text size="medium">{name}</Text>
                </Flex>
                <Text size="small" color="color.text.subtlest">
                  {entitlementNumber}
                </Text>
              </Flex>
            );
          }
          return null;
        })
      : null;

  const orgInstancesRows =
    orgInstances && orgInstances?.length > 0
      ? orgInstances.map(({ id, name, entitlementNumber, logoId, productId, containerId }) => {
          const allAppsOfInstance = appsData?.[containerId ?? id];
          const lengthOfApps = allAppsOfInstance?.length;
          return (
            <Flex xcss={appRowStyles({ isDisabled })} alignItems="center" justifyContent="space-between" key={id}>
              <Flex alignItems="center">
                <EntitlementInstancesIcon>
                  <EntitlementIcon width="16px" height="16px" productId={productId} logoId={logoId} isCloud />
                </EntitlementInstancesIcon>
                <Text size="medium">{name}</Text>
              </Flex>
              {lengthOfApps && (
                <Text size="small" color="color.text.subtlest">
                  Apps / add ons <Badge>{lengthOfApps}</Badge>
                </Text>
              )}
              <Text size="small" color="color.text.subtlest">
                {entitlementNumber}
              </Text>
            </Flex>
          );
        })
      : null;

  return version === 2 ? (
    <>
      {productRow}
      {isTWCPhase2Enabled && isExpanded && orgInstancesRows}
      {isExpanded && appRows}
    </>
  ) : (
    <div className="cart-entitlement-item-v2 flex justify-space-between align-center">
      <div className="cart-entitlement-item-details flex align-center ml-16">
        <div className="cart-entitlement-item-product-icon">
          <EntitlementIcon width="24px" height="24px" productId={productId} isCloud />
        </div>
        <div className="flex-column ml-12 cart-entitlement-item-name">
          <div className="flex entitlement-name">
            <div
              className={classNames({
                'entitlement-name-text': shouldShowTrialStatus && inTrial,
              })}
              title={name}
              data-testId="entitlement-name"
            >
              {name}
            </div>
            {isInactive ? (
              <span data-testId="entitlement-inactive-lozenge">
                <InactiveLozenge label={'Cancelled'} />
              </span>
            ) : null}
            {shouldShowSandboxStatus && isSandbox && productId !== ROVO_PRODUCT_ID && (
              <span data-testId="entitlement-sandbox-lozenge">
                <SandboxLozenge />
              </span>
            )}
            {shouldShowTrialStatus && inTrial && (
              <span data-testId="entitlement-trial-lozenge">
                <TrialLozenge />
              </span>
            )}
          </div>
          <div className="entitlement-id">{entitlementNumber}</div>
        </div>
      </div>
      <div className="cart-entitlement-item-v2-selection">
        {isChecked ? (
          <CTAButton
            testId={'entitlement-selected-cta-btn'}
            className="entitlement-selected-cta-btn cta-btn"
            onClick={() => _onChange(false)}
            isDisabled={isDisabled}
            iconBefore={
              <>
                <span className="entitlement-select-cta-btn">
                  <CheckIcon label="Selected entitlement" size="large" />
                </span>
                <span className="entitlement-cancel-cta-btn">
                  <CrossIcon label="Cancel entitlement" size="small" />
                </span>
              </>
            }
          />
        ) : (
          <CTAButton
            testId={'entitlement-add-cta-btn'}
            className="entitlement-add-cta-btn cta-btn"
            onClick={() => _onChange(true)}
            isDisabled={isDisabled}
            iconBefore={<AddIcon label="Add" size="small" data-testId="add-cta-icon" />}
          />
        )}
      </div>
      {shouldShowAppsCount && linkedEntitlements?.length > 0 && (
        <div className="entitlement-integrated-apps">
          <Lozenge testId={'product-integrated-apps'} appearance={'default'}>
            {pluralize(linkedEntitlements.length, 'Integrated app')}
          </Lozenge>
        </div>
      )}
      {shouldShowAddons && addons?.length > 0 && (
        <div className="entitlement-integrated-apps">
          <Lozenge testId={'product-integrated-apps'} appearance={'default'}>
            {pluralize(addons.length, 'Add-on')}
          </Lozenge>
        </div>
      )}
    </div>
  );
};
